import {
  collapseToRanges,
  expandCodeList,
  intersectCodes,
} from "helpers/codeRanges";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Chips } from "primereact/chips";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import { ChargeBasedPricingScope } from "types/ChargeBasedPricing/ChargeBasedPricing";

import { sanitizeDrgCodes } from "./InpatientRulesetForm";

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FieldHelp = styled.span`
  font-size: 0.85rem;
  color: var(--gray-600);
`;

const CheckboxLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const SCOPE_OPTIONS: { label: string; value: ChargeBasedPricingScope }[] = [
  { label: "Line charges", value: "LINE_CHARGES" },
  { label: "Total charges", value: "TOTAL_CHARGES" },
];

export interface IChargeBasedPricingFormData {
  scope: ChargeBasedPricingScope;
  multiplier: number;
  // Optional max-amount cap, in dollars (converted to cents at the submit boundary).
  // Null = no cap; multiplier alone determines the expected.
  maxAmountInDollars: number | null;
  providerId: string | null;
  payerId: number | null;
  codes: string[];
  excludedCodes: string[];
  groupName: string;
  isDefault: boolean;
}

// Most charge-based-pricing contracts are whole-claim, percent-of-charges deals
// (e.g. "45% of charges, no fee schedule"), so default scope to TOTAL_CHARGES.
export const INITIAL_CHARGE_BASED_PRICING_FORM_DATA: IChargeBasedPricingFormData =
  {
    scope: "TOTAL_CHARGES",
    multiplier: 1,
    maxAmountInDollars: null,
    providerId: null,
    payerId: null,
    codes: [],
    excludedCodes: [],
    groupName: "",
    isDefault: false,
  };

interface IChargeBasedPricingFormProps {
  title: string;
  submitLabel: string;
  formData: IChargeBasedPricingFormData;
  onChange: (data: IChargeBasedPricingFormData) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** In edit mode, `isDefault` is the discriminator for scope and is locked. */
  scopeLocked?: boolean;
}

export function ChargeBasedPricingForm({
  title,
  submitLabel,
  formData,
  onChange,
  onSubmit,
  isSubmitting,
  scopeLocked,
}: IChargeBasedPricingFormProps) {
  const navigate = useNavigate();
  const { data: providers } = useGetAllProvidersQuery();
  const { data: payers } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providers?.data) return [];
    return providers.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providers]);

  const payerOptions = useMemo(() => {
    const base = (payers?.data ?? []).map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
    return [{ label: "All payers", value: null }, ...base];
  }, [payers]);

  const handleChange = <K extends keyof IChargeBasedPricingFormData>(
    field: K,
    value: IChargeBasedPricingFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  const isValid =
    !!formData.scope &&
    (formData.isDefault || formData.providerId !== null) &&
    !isSubmitting;

  // Same overlap-warning logic as lesser-of-charges — expand ranges first so codes
  // hidden inside a "0300-0420" range still show up as overlaps, then collapse the
  // intersection back to ranges for the human-readable warning text.
  const overlappingCodes = useMemo(
    () =>
      collapseToRanges(
        intersectCodes(
          expandCodeList(formData.codes),
          expandCodeList(formData.excludedCodes)
        )
      ),
    [formData.codes, formData.excludedCodes]
  );

  return (
    <div className="p-4">
      <div className="flex align-items-center mb-4">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text mr-2"
          onClick={() => navigate("/payer-rules")}
          aria-label="Back"
        />
        <h2 className="m-0">{title}</h2>
      </div>

      {/* Important framing: this rule REPLACES standard pricing, it doesn't cap it.
          Worth surfacing prominently so users don't confuse it with lesser-of-charges. */}
      <Message
        severity="warn"
        className="mb-4"
        content={
          <div>
            <strong>Charge-Based Pricing replaces the standard pricing</strong>{" "}
            for matching claims. When this rule fires, the expected amount is
            computed as <code>multiplier × billed charges</code>, ignoring any
            fee schedule, inpatient outliers, or lesser-of cap. Use this for
            contracts that pay purely as a percentage of charges with no fee
            schedule.
          </div>
        }
      />

      <Card className="mb-4">
        <FieldGrid>
          <Field>
            <label htmlFor="scope">Scope</label>
            <Dropdown
              inputId="scope"
              value={formData.scope}
              options={SCOPE_OPTIONS}
              onChange={(e) => handleChange("scope", e.value)}
              placeholder="Select scope"
            />
            <FieldHelp>
              Total charges prices the claim as a whole; Line charges prices
              each CPT-bearing line.
            </FieldHelp>
          </Field>
          <Field>
            <label htmlFor="multiplier">Multiplier</label>
            <InputNumber
              inputId="multiplier"
              value={formData.multiplier}
              onValueChange={(e) => handleChange("multiplier", e.value ?? 1)}
              mode="decimal"
              minFractionDigits={2}
              maxFractionDigits={4}
              min={0}
              max={99}
            />
            <FieldHelp>
              0.45 = 45% of charges, 1.00 = 100% of charges.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <FieldGrid>
          <Field>
            <label htmlFor="maxAmount">Max amount (optional)</label>
            <InputNumber
              inputId="maxAmount"
              value={formData.maxAmountInDollars}
              onValueChange={(e) =>
                handleChange(
                  "maxAmountInDollars",
                  e.value === undefined ? null : e.value
                )
              }
              mode="currency"
              currency="USD"
              locale="en-US"
              min={0}
              placeholder="Leave empty for no cap"
            />
            <FieldHelp>
              Ceiling on the priced expected per claim. Example: &quot;78% of
              charges, capped at $1,500&quot; → multiplier 0.78, max amount
              $1,500. Leave empty for no cap.
            </FieldHelp>
          </Field>
        </FieldGrid>

        <CheckboxLine>
          <Checkbox
            inputId="isDefault"
            checked={formData.isDefault}
            disabled={scopeLocked}
            onChange={(e) => handleChange("isDefault", !!e.checked)}
          />
          <label htmlFor="isDefault">
            Default rule (applies to all providers and payers unless overridden)
          </label>
        </CheckboxLine>

        {!formData.isDefault && (
          <FieldGrid>
            <Field>
              <label htmlFor="providerId">Provider</label>
              <Dropdown
                inputId="providerId"
                value={formData.providerId}
                options={providerOptions}
                onChange={(e) => handleChange("providerId", e.value ?? null)}
                placeholder="Select a provider"
                filter
                disabled={scopeLocked}
              />
            </Field>
            <Field>
              <label htmlFor="payerId">Payer</label>
              <Dropdown
                inputId="payerId"
                value={formData.payerId}
                options={payerOptions}
                onChange={(e) => handleChange("payerId", e.value ?? null)}
                placeholder="Select a payer"
                filter
                disabled={scopeLocked}
              />
              <FieldHelp>
                Leave as &quot;All payers&quot; to apply this rule to every
                payer for this provider.
              </FieldHelp>
            </Field>
          </FieldGrid>
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="codes">Codes</label>
            <Chips
              inputId="codes"
              value={formData.codes}
              onChange={(e) =>
                handleChange("codes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="96523, J1642, 470, 0272-0372"
              separator=","
            />
            <FieldHelp>
              Enter individual codes (96523, J1642) or ranges (0272-0372). The
              rule applies to any claim that contains at least one of these
              codes. Leave empty to price every claim from this provider/payer
              at <code>multiplier × charges</code> (the most common case).
            </FieldHelp>
          </Field>
          <Field>
            <label htmlFor="excludedCodes">Excluded Codes</label>
            <Chips
              inputId="excludedCodes"
              value={formData.excludedCodes}
              onChange={(e) =>
                handleChange("excludedCodes", sanitizeDrgCodes(e.value ?? []))
              }
              placeholder="99213, 470"
              separator=","
            />
            <FieldHelp>
              Codes to skip from the rule. If a code here matches the claim at
              the header level, the rule does not apply at all. Service lines
              whose code matches one here are dropped from the pricing
              calculation.
            </FieldHelp>
          </Field>
        </FieldGrid>

        {overlappingCodes.length > 0 && (
          <Message
            severity="warn"
            className="mb-4"
            text={`Code${
              overlappingCodes.length > 1 ? "s" : ""
            } ${overlappingCodes.join(", ")} appear${
              overlappingCodes.length === 1 ? "s" : ""
            } in both lists — Excluded Codes will win.`}
          />
        )}

        <FieldGrid>
          <Field>
            <label htmlFor="groupName">Description</label>
            <InputText
              id="groupName"
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              placeholder="Optional contract reference (e.g. 'Lake Charles Employee')"
            />
          </Field>
        </FieldGrid>

        <Footer>
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => navigate("/payer-rules")}
          />
          <Button
            label={submitLabel}
            icon="pi pi-check"
            onClick={onSubmit}
            disabled={!isValid}
            loading={isSubmitting}
          />
        </Footer>
      </Card>
    </div>
  );
}
